import React from "react"
import { Button } from "react-bootstrap"

import {
  isLTPM,
  injectIntl,
  RunMatchSelectionLayer,
  getFormattedMessage,
} from "../../../components/shared_components_registration"
import RunEventLabelSelectionLayer from "./widgets/ToggleRunLabelsButton/RunEventLabelSelectionLayer"
import YAxis from "./GraphComponents/YAxis"
import AnnotationsLayer from "./GraphComponents/AnnotationsLayer"
import CriticalActionsLayer from "./GraphComponents/CriticalActionsLayer"
import CrossHairLayer from "./GraphComponents/CrossHairLayer"
import ActionLayer from "./GraphComponents/ActionLayer"
import ToggleActionLayers from "./GraphComponents/ToggleActionLayers"
import XAxis from "./GraphComponents/XAxis"
import ZoomSelectionLayer from "../zooming/ZoomSelectionLayer"
import { getPlotHeight } from "../resources/utils"
import LoadingIndicator from "../resources/LoadingIndicator"

function AbstractPlot({
  intl,
  clipID,
  width,
  height,
  margin,
  timeExtractor,
  xNotTime = false, // When true (for SeriesPlot only!) to indicate X-Axis is NOT a timeseries
  x,
  xAxisLabel,
  xFormatter,
  showY1 = true,
  y1,
  y1AxisLabel,
  y1Formatter = (x) => x.toString(),
  showY2 = false,
  y2,
  y2AxisLabel,
  y2Formatter = (x) => x.toString(),
  sharedYAxis = false,
  zoomKey,
  baseTime,
  showGraphTitle = false,
  graphKey = {},
  graphTitleSupplement = "",
  titleFontSize,
  showSVGGridlines = true, // If true we draw gridlines using svg (otherwise they're drawn on the canvas or not at all)
  isSuppPlot = false,
  tickNum,
  PlotCanvas,
  PlotSVG,
  download = false,
  loading = false,
  showActionsAnnotations = false,
  runInoculationDate,
  actions = [],
  site_actions = [],
  annotations = [],
  onCrossHairClick,
  findDataForPoint,
  forceReloadPlotData = () => {},
  errorLayers = [],
  runEventLabels = [],
  avoid_annotations = false,
}) {
  const unknown_label = `[${getFormattedMessage("general.unknown", "Unknown", true, intl)}]`
  return (
    /*
     * Old note regarding why canvas/svg trend elements are not placed within the same
     * <g transform={`translate(${margin.left}, ${margin.top})`} /> element :
     * Keeping the canvas *outside* the SVG due to foreignObject
     * bugs in both Blink and Webkit. Has not been revisited in recent days
     */
    <div style={{ position: "relative" }}>
      {/*
        Canvas presentation of data trends, gridlines, and vertical dashed lines - does NOT contain axes,
        labels, or titles. Plot trends only on canvas. A more lightweight view on a webpage than using SVG
        elements.
      */}
      {PlotCanvas}

      {/*
        svg contains:
          -x, y, and y2 (if applicable) axes, labels, and tick marks
          -graph title (if applicable/we have a desire to show)
          -SVG drawn trends (if we choose to draw/present SVG rather than the canvas version of the trends)
          -CrossHairLayer, ZoomSelectionLayer, ActionLayer - plot components
      */}
      <svg
        width={width + margin.left + margin.right}
        height={
          timeExtractor ? getPlotHeight(height, margin, timeExtractor) : height + margin.top + margin.bottom // If X-Axis is timestamp based give more label space
        }
        style={{
          userSelect: "none",
          position: "relative" /* Not obviously necessary, but because the canvas has to be absolutely
                                  positioned within its layer, we need a positioning rule here to ensure
                                  that the SVG layer gets z-stacked above the canvas (for graphTypes with
                                  a canvas option. Not all have one, but it doesn't hurt otherwise) */,
        }}
      >
        <defs>
          <clipPath id={clipID}>
            <rect x={0} y={0} width={width} height={height} />
          </clipPath>
        </defs>

        <g transform={`translate(${margin.left}, ${margin.top})`}>
          {showGraphTitle && (
            <g transform={`translate(${width / 2}, ${0 - margin.top / 2})`}>
              <text fill="black" textAnchor="middle" fontFamily="Arial" fontSize={titleFontSize}>
                {graphKey.label ? graphKey.label : graphKey.title}
                {graphTitleSupplement && <span>{` - ${graphTitleSupplement}`}</span>}
              </text>
            </g>
          )}

          <g transform={`translate(0, ${height})`}>
            <XAxis
              x={x}
              height={height}
              width={width}
              gridlines={showSVGGridlines}
              label={
                xAxisLabel
                  ? xAxisLabel
                  : timeExtractor.name === "Day"
                    ? getFormattedMessage("graphing.timelabel.day", "Day", true, intl)
                    : ""
              }
              formatter={
                xFormatter
                  ? xFormatter
                  : timeExtractor.name === "Day"
                    ? timeExtractor.formatter
                    : timeExtractor.verboseFormatter
              }
              timeExtractor={timeExtractor}
              isSuppPlot={isSuppPlot}
            />
          </g>

          {showY1 && (
            <YAxis
              y={y1}
              width={width}
              gridlines={showSVGGridlines}
              label={y1AxisLabel || unknown_label}
              tickNum={tickNum}
              flipped={false}
              formatter={y1Formatter}
              isSuppPlot={isSuppPlot}
            />
          )}

          {showY2 && (
            <g transform={`translate(${width}, 0)`}>
              <YAxis
                y={sharedYAxis ? y1 : y2}
                width={width}
                gridlines={showSVGGridlines && !showY1}
                label={y2AxisLabel || unknown_label}
                tickNum={tickNum}
                flipped={true}
                formatter={y2Formatter}
                isSuppPlot={isSuppPlot}
              />
            </g>
          )}

          <g clipPath={`url(#${clipID})`}>
            {PlotSVG}

            {showActionsAnnotations && (
              <>
                <AnnotationsLayer x={x} y={y1} annotations={annotations} />
                <CriticalActionsLayer x={x} y={y1} actions={actions} />
              </>
            )}

            {!download && (showY1 || showY2) && (
              <CrossHairLayer
                x={x}
                y={y1}
                y2={y2}
                xNotTime={xNotTime}
                graphKey={graphKey}
                baseTime={baseTime}
                onClick={onCrossHairClick}
                findDataForPoint={findDataForPoint}
                runEventLabels={runEventLabels}
              />
            )}
          </g>
        </g>

        {!xNotTime && <RunMatchSelectionLayer x={x} y={y1} y2={y2} margin={margin} />}

        {!xNotTime && !isLTPM && !avoid_annotations && !["multi"].includes(graphKey.special) && (
          <RunEventLabelSelectionLayer
            x={x}
            y={y1}
            y2={y2}
            margin={margin}
            zoomKey={zoomKey}
            graphKey={graphKey}
          />
        )}

        <ZoomSelectionLayer
          x={x}
          y={y1}
          y2={y2}
          margin={margin}
          zoomKey={zoomKey}
          zoomGraphKey={graphKey}
          baseTime={baseTime}
          xNotTime={xNotTime}
        />

        {!download && showActionsAnnotations && (
          <ActionLayer // X-Axis triangles and tooltip
            runInoculationDate={runInoculationDate}
            actions={actions}
            site_actions={site_actions}
            annotations={annotations}
            x={x}
            download={download}
            margin={margin}
            height={height}
            clipID={clipID}
          />
        )}
      </svg>

      {!download && showActionsAnnotations && <ToggleActionLayers download={download} height={height} />}

      <div
        style={{
          display: loading ? "block" : "none",
          position: "absolute",
          top: "0px",
          bottom: "0px",
          left: "0px",
          width: `${width + margin.left + margin.right}px`,
        }}
      >
        <LoadingIndicator />
      </div>

      {errorLayers.length > 0 && (
        <div style={{ display: "flex" }}>
          <div>
            {!download && forceReloadPlotData && (
              <Button
                variant={"outline-primary"}
                disabled={loading}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  forceReloadPlotData()
                }}
              >
                <span className={"fa fa-refresh"} />
              </Button>
            )}
          </div>
          &nbsp;&nbsp;
          <div>
            {errorLayers.map(({ tag, err }, idx) => (
              <div key={idx}>
                <p className={"error-message"} style={{ marginBottom: "0" }}>
                  {`${err.toString()}: ${tag}`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default injectIntl(AbstractPlot)
